import { Component } from '@theme/component';

/**
 * @typedef {Object} ContactRoutedFormRefs
 * @property {HTMLInputElement[]} routes - The route radios.
 * @property {HTMLElement} [status] - Live region announcing which extra field is showing.
 */

/**
 * A contact form whose payload changes with the route the customer picks.
 *
 * Every piece is quantity 1 and most of the catalogue carries no reference number, so an
 * enquiry that does not say WHICH piece cannot be answered. The product page links here with
 * `?about=piece&piece=<title>&ref=<handle>`; this fills the piece field and a hidden link so
 * the enquiry arrives already identified.
 *
 * Fields render visible and enabled, then this hides the ones the current route does not use.
 * That order is deliberate: with JavaScript unavailable every field stays reachable and the
 * form still works, it just asks for a little more typing.
 *
 * @extends {Component<ContactRoutedFormRefs>}
 */
class ContactRoutedForm extends Component {
  // The markup writes ref="routes[]"; Component strips the brackets, so the required name is
  // the bare key. Asking for "routes[]" here throws MissingRefError and takes the form with it.
  requiredRefs = ['routes'];

  connectedCallback() {
    super.connectedCallback();
    this.#applyLinkedIntent();
    this.#update();
  }

  /** Bound from each route radio's `on:change`. */
  selectRoute() {
    this.#update();
  }

  /** @returns {NodeListOf<HTMLElement>} The conditional field groups. */
  get #groups() {
    return this.querySelectorAll('[data-route-field]');
  }

  /** @returns {string | undefined} The checked route's key. */
  get #route() {
    return this.refs.routes.find((radio) => radio.checked)?.dataset.route;
  }

  /**
   * Show the group belonging to the current route, hide and DISABLE the rest. Disabling
   * matters as much as hiding: a hidden field still posts, so without it every enquiry would
   * arrive carrying empty fields for the routes the customer did not choose.
   */
  #update() {
    const route = this.#route;
    let showing = '';

    for (const group of this.#groups) {
      const active = group.dataset.routeField === route;
      group.hidden = !active;

      for (const field of group.querySelectorAll('input, textarea, select')) {
        field.disabled = !active;
      }

      if (active) showing = group.dataset.routeAnnounce ?? '';
    }

    if (this.refs.status) this.refs.status.textContent = showing;
  }

  /**
   * Read the route and the piece out of the link that brought the customer here.
   *
   * Everything from the query string is assigned as a VALUE, never as markup, and the handle
   * is checked against the shape Shopify actually issues before it is used to build a URL.
   */
  #applyLinkedIntent() {
    const params = new URLSearchParams(window.location.search);

    const about = params.get('about');
    const radio = about && this.refs.routes.find((r) => r.dataset.route === about);
    if (radio) radio.checked = true;

    const piece = params.get('piece');
    const nameField = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-piece-name]'));
    if (piece && nameField && !nameField.value) nameField.value = piece.slice(0, 200);

    const handle = params.get('ref');
    const linkField = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-piece-link]'));
    if (handle && linkField && /^[a-z0-9][a-z0-9-]{0,120}$/.test(handle)) {
      // routes.root_url is "/" on the primary locale and "/en-gb" on the others, so trim
      // the trailing slash before joining rather than assuming either shape.
      const root = (this.dataset.root || '/').replace(/\/$/, '');
      linkField.value = new URL(`${root}/products/${handle}`, window.location.origin).href;
    }
  }
}

if (!customElements.get('contact-routed-form')) {
  customElements.define('contact-routed-form', ContactRoutedForm);
}
